/**
 * Order-flow lines from flow-signals (market volume imbalance, stop volume,
 * book imbalance, average liquidity, price dynamics), relayed by the engine.
 * Visual only: the chart draws them in panes under the price.
 *
 * Contract (utt-engine/src/flow_signals):
 * - Socket, client -> server: `flow-lines:subscribe` / `flow-lines:unsubscribe` {symbol}
 *   join / leave the opt-in room `flow-lines:<root>`.
 * - Socket "flow-line" -> {symbol, root, contract, name, timestamp (ms), value}, at most 1/s per line.
 * - REST GET {api}/flow-lines/{symbol}?names=a,b&minutes=480 -> {symbol, contract, lines: {name: [[ms, value], ...]}}
 *
 * A freshly joined room gets nothing until the next sample and a reconnect
 * loses every room, so history always comes over REST: when a chart starts
 * watching a line, (with the subscribe re-emitted) after every socket
 * reconnect, and after the engine's relay reconnects to flow-signals.
 *
 * Rooms are per root on the engine (`MNQ`, `MNQZ6.CME` and, in roll week,
 * `MNQH7.CME` all join `flow-lines:MNQ`), so watches are counted per root too:
 * one chart leaving never unsubscribes another chart that spells the symbol
 * (or the contract) differently. The engine's lines for a root follow one
 * contract at a time and say which (`contract`); when that changes the held
 * history is dropped, not merged, so a line never mixes two contracts.
 *
 * Visibility and colours live in levelsStore (`flowLine*` keys).
 */
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { normalizeToBase } from '../utils/symbolUtils';
import { useMarketStore } from './marketStore';
import { onFlowHistoryGap } from './flowSignalsStore';

export type FlowLineName =
  | 'market_volume_imbalance'
  | 'stop_volume'
  | 'order_book_imbalance'
  | 'average_liquidity'
  | 'price_dynamics';

/**
 * Lines the store carries that are not drawn as panes: `queue_tilt` (smoothed
 * top-of-book queue imbalance, -1..1) feeds the queue-tilt band at the price
 * scale on ES / MES charts (Chart/queueTilt.ts), never a pane.
 */
export type FlowContextLineName = 'queue_tilt';
export const FLOW_CONTEXT_LINE_NAMES: readonly FlowContextLineName[] = ['queue_tilt'];

/** Any line the store holds: pane lines and context lines. */
export type FlowAnyLineName = FlowLineName | FlowContextLineName;

export type FlowLineKey =
  | 'flowLineMvi'
  | 'flowLineStopVolume'
  | 'flowLineBookImbalance'
  | 'flowLineAvgLiquidity'
  | 'flowLinePriceDynamics';

/** Lines that share a pane: volume (MVI + stop volume), book, liquidity (+ price dynamics). */
export type FlowLineGroup = 'volume' | 'book' | 'liquidity';

export interface FlowLineConfig {
  name: FlowLineName;
  key: FlowLineKey;
  group: FlowLineGroup;
  label: string;
  /** Price-axis title. */
  short: string;
  description: string;
}

/** Every line, in pane order (top to bottom) and in Lines-tab order. */
export const FLOW_LINES: readonly FlowLineConfig[] = [
  {
    name: 'market_volume_imbalance', key: 'flowLineMvi', group: 'volume', label: 'Market volume imbalance', short: 'MVI',
    description: 'Decayed aggressive buy minus sell volume; above zero = buyers in control',
  },
  {
    name: 'stop_volume', key: 'flowLineStopVolume', group: 'volume', label: 'Stop volume', short: 'Stops',
    description: 'Decayed buy-stop minus sell-stop volume; above zero = buy stops (shorts covering) dominate',
  },
  {
    name: 'order_book_imbalance', key: 'flowLineBookImbalance', group: 'book', label: 'Order book imbalance', short: 'Book',
    description: 'Smoothed resting depth imbalance between the book sides (own pane)',
  },
  {
    name: 'average_liquidity', key: 'flowLineAvgLiquidity', group: 'liquidity', label: 'Average liquidity', short: 'Liq',
    description: 'Average resting size near the price',
  },
  {
    name: 'price_dynamics', key: 'flowLinePriceDynamics', group: 'liquidity', label: 'Price dynamics', short: 'PxDyn',
    description: 'How easily price moves through the book (shares the liquidity pane, own scale)',
  },
];

export const FLOW_LINE_KEYS: readonly FlowLineKey[] = FLOW_LINES.map((l) => l.key);

/** [exchange time ms, value], ascending by time. */
export type FlowLinePoint = readonly [number, number];

/** History requested from the engine (its cache holds 8h). */
export const FLOW_LINE_HISTORY_MIN = 480;
/** Points kept per (root, line): 8h at the engine's 1/s, plus slack. */
export const MAX_FLOW_LINE_POINTS = 30_000;
/** Live points are applied in batches this far apart, ms. */
export const FLOW_LINE_FLUSH_MS = 250;

const KNOWN = new Set<string>([...FLOW_LINES.map((l) => l.name), ...FLOW_CONTEXT_LINE_NAMES]);

/** A line the store keeps (pane line or context line). */
export function isFlowLineName(name: unknown): name is FlowAnyLineName {
  return typeof name === 'string' && KNOWN.has(name);
}

interface FlowLineMessage {
  symbol: string;
  name: FlowAnyLineName;
  timestamp: number;
  value: number;
  /** The contract the root's lines follow (engine >= roll-week fix); absent from older engines. */
  contract?: string;
}

export function isFlowLineMessage(m: unknown): m is FlowLineMessage {
  if (!m || typeof m !== 'object') return false;
  const c = m as Partial<FlowLineMessage>;
  return (
    typeof c.symbol === 'string' &&
    isFlowLineName(c.name) &&
    typeof c.timestamp === 'number' && Number.isFinite(c.timestamp) &&
    typeof c.value === 'number' && Number.isFinite(c.value)
  );
}

const isPoint = (p: unknown): p is FlowLinePoint =>
  Array.isArray(p) && p.length >= 2 &&
  typeof p[0] === 'number' && Number.isFinite(p[0]) &&
  typeof p[1] === 'number' && Number.isFinite(p[1]);

const capLine = (list: FlowLinePoint[]): FlowLinePoint[] =>
  list.length > MAX_FLOW_LINE_POINTS ? list.slice(list.length - MAX_FLOW_LINE_POINTS) : list;

/**
 * Merge points into a line: ascending by time, one value per timestamp (the
 * incoming one wins), capped (newest kept). Live points are nearly always
 * newer than everything held, so that case appends without re-sorting.
 * Returns `existing` itself when nothing valid came in.
 */
export function mergeLinePoints(existing: readonly FlowLinePoint[], incoming: readonly unknown[]): readonly FlowLinePoint[] {
  const fresh = new Map<number, FlowLinePoint>();
  for (const p of incoming) if (isPoint(p)) fresh.set(p[0], [p[0], p[1]]);
  if (fresh.size === 0) return existing;
  const add = [...fresh.values()].sort((a, b) => a[0] - b[0]);
  const lastTs = existing.length > 0 ? existing[existing.length - 1][0] : -Infinity;
  if (add[0][0] > lastTs) return capLine(existing.concat(add));
  // Out of order or overlapping: only the tail from the first incoming time moves.
  let tailStart = existing.length;
  while (tailStart > 0 && existing[tailStart - 1][0] >= add[0][0]) tailStart -= 1;
  const byTs = new Map<number, FlowLinePoint>();
  for (let i = tailStart; i < existing.length; i += 1) byTs.set(existing[i][0], existing[i]);
  for (const p of add) byTs.set(p[0], p);
  const merged = [...byTs.values()].sort((a, b) => a[0] - b[0]);
  return capLine(existing.slice(0, tailStart).concat(merged));
}

/**
 * Minutes of history to ask for after a reconnect: back to the oldest of the
 * newest points held for these lines (plus a minute), the full window when
 * any of them has nothing.
 */
export function backfillMinutes(
  lines: Partial<Record<FlowAnyLineName, readonly FlowLinePoint[]>> | undefined,
  names: readonly FlowAnyLineName[],
  now: number,
): number {
  let oldestLast = Infinity;
  for (const n of names) {
    const l = lines?.[n];
    if (!l || l.length === 0) return FLOW_LINE_HISTORY_MIN;
    oldestLast = Math.min(oldestLast, l[l.length - 1][0]);
  }
  if (!Number.isFinite(oldestLast)) return FLOW_LINE_HISTORY_MIN;
  const minutes = Math.ceil(Math.max(0, now - oldestLast) / 60_000) + 1;
  return Math.min(FLOW_LINE_HISTORY_MIN, Math.max(1, minutes));
}

// ------------------------------------------------------------- store

export type FlowLinesByName = Partial<Record<FlowAnyLineName, readonly FlowLinePoint[]>>;

interface FlowLinesState {
  /** Keyed by base symbol (NQ, MNQ, ES, MES), then line name. */
  byRoot: Record<string, FlowLinesByName>;
  /**
   * Per root, the contract the held lines came from (engine `contract`). The
   * engine's lines follow one contract per root; when it switches (a roll),
   * the held history is from the old contract and is dropped, never merged.
   */
  contractByRoot: Record<string, string>;

  /** Live points (socket payloads); invalid ones are dropped. */
  addPoints: (messages: readonly unknown[]) => void;
  /** REST body `lines` (and `contract`) for `symbol`. */
  addHistory: (symbol: string, lines: Record<string, unknown>, contract?: string | null) => void;
  fetchLines: (symbol: string, names: readonly FlowAnyLineName[], minutes?: number) => Promise<void>;
  /**
   * A chart draws `names` for `symbol`: join the room (first watcher), load
   * history for lines not loaded yet; the returned function undoes it (last
   * watcher leaves the room).
   */
  watchLines: (symbol: string, names: readonly FlowAnyLineName[]) => () => void;
  /** Reference-counted: every chart showing a line calls both. */
  initialize: () => void;
  teardown: () => void;
}

interface SymbolWatch {
  /** The spelling sent to the engine (the first watcher's); any resolves to the root's room. */
  symbol: string;
  count: number;
  names: Map<FlowAnyLineName, number>;
  /** Lines whose history was requested since the room was joined. */
  loaded: Set<FlowAnyLineName>;
}

/** Keyed by root (normalizeToBase), the engine's room granularity. */
const watches = new Map<string, SymbolWatch>();
let users = 0;
let unsubMarket: (() => void) | null = null;
let unsubRelay: (() => void) | null = null;
let pending: unknown[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let warned = false;

const flush = (): void => {
  flushTimer = null;
  const batch = pending;
  pending = [];
  if (batch.length > 0) useFlowLinesStore.getState().addPoints(batch);
};

/**
 * Feed line points as if they arrived on the socket (DEV builds only: the
 * staging Vite server), e.g. a queue_tilt value before the service streams
 * one. Playwright: `window.__uttFlowLineInject([{symbol, name, timestamp, value}])`.
 */
export function injectFlowLinesForTest(messages: readonly unknown[]): void {
  useFlowLinesStore.getState().addPoints(messages);
}

if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  (window as unknown as { __uttFlowLineInject?: (m: unknown[]) => void }).__uttFlowLineInject = (m) => injectFlowLinesForTest(m);
}

const onLine = (m: unknown): void => {
  if (!isFlowLineMessage(m)) return;
  pending.push(m);
  if (flushTimer === null) flushTimer = setTimeout(flush, FLOW_LINE_FLUSH_MS);
};

/** (Re)bind the socket handler: a forced reconnect builds a new socket. */
function bindSocket(): void {
  if (!wsService.isConnected()) return; // bound on the next 'connected' instead
  wsService.off('flow-line', onLine);
  wsService.on('flow-line', onLine);
}

/** Drop lines nobody watches (reloaded if watched again) and leave an unwatched room. */
function prune(root: string, w: SymbolWatch): void {
  if (watches.get(root) !== w) return;
  for (const [n, c] of w.names) {
    if (c > 0) continue;
    w.names.delete(n);
    w.loaded.delete(n);
  }
  if (w.count > 0) return;
  watches.delete(root);
  unsubscribeRoom(w.symbol);
}

/** Lines some chart still draws for this watch. */
const watchedNames = (w: SymbolWatch): FlowAnyLineName[] => [...w.names].filter(([, c]) => c > 0).map(([n]) => n);

const subscribeRoom = (symbol: string): void => {
  if (wsService.isConnected()) wsService.emit('flow-lines:subscribe', { symbol });
};

const unsubscribeRoom = (symbol: string): void => {
  if (wsService.isConnected()) wsService.emit('flow-lines:unsubscribe', { symbol });
};

export const useFlowLinesStore = create<FlowLinesState>((set, get) => ({
  byRoot: {},
  contractByRoot: {},

  addPoints: (messages) =>
    set((s) => {
      // root -> name -> points
      const groups = new Map<string, Map<FlowAnyLineName, FlowLinePoint[]>>();
      const contracts = new Map<string, string>();
      for (const m of messages) {
        if (!isFlowLineMessage(m)) continue;
        const root = normalizeToBase(m.symbol);
        const c = typeof m.contract === 'string' && m.contract ? m.contract : null;
        if (c && contracts.get(root) !== c) {
          // Within one batch a switch drops the points before it too.
          if (contracts.has(root)) groups.delete(root);
          contracts.set(root, c);
        }
        let byName = groups.get(root);
        if (!byName) groups.set(root, (byName = new Map()));
        const list = byName.get(m.name);
        if (list) list.push([m.timestamp, m.value]);
        else byName.set(m.name, [[m.timestamp, m.value]]);
      }
      if (groups.size === 0) return s;
      const next = { ...s.byRoot };
      let contractByRoot = s.contractByRoot;
      for (const [root, byName] of groups) {
        const c = contracts.get(root);
        const switched = c !== undefined && s.contractByRoot[root] !== undefined && s.contractByRoot[root] !== c;
        if (c !== undefined && s.contractByRoot[root] !== c) contractByRoot = { ...contractByRoot, [root]: c };
        const lines: FlowLinesByName = switched ? {} : { ...next[root] };
        for (const [name, pts] of byName) lines[name] = mergeLinePoints(lines[name] ?? [], pts);
        next[root] = lines;
      }
      return { byRoot: next, contractByRoot };
    }),

  addHistory: (symbol, lines, contract) =>
    set((s) => {
      const root = normalizeToBase(symbol);
      const known = s.contractByRoot[root];
      const c = typeof contract === 'string' && contract ? contract : undefined;
      // History from another contract than the held lines: replace, not merge.
      const switched = c !== undefined && known !== undefined && known !== c;
      const current: FlowLinesByName = switched ? {} : s.byRoot[root] ?? {};
      const updated: FlowLinesByName = { ...current };
      let changed = switched;
      for (const [name, pts] of Object.entries(lines ?? {})) {
        if (!isFlowLineName(name) || !Array.isArray(pts)) continue;
        const merged = mergeLinePoints(current[name] ?? [], pts);
        if (merged !== current[name]) {
          updated[name] = merged;
          changed = true;
        }
      }
      const contractByRoot = c !== undefined && known !== c ? { ...s.contractByRoot, [root]: c } : s.contractByRoot;
      if (!changed) return contractByRoot === s.contractByRoot ? s : { contractByRoot };
      return { byRoot: { ...s.byRoot, [root]: updated }, contractByRoot };
    }),

  fetchLines: async (symbol, names, minutes = FLOW_LINE_HISTORY_MIN) => {
    if (!symbol || names.length === 0) return;
    const root = normalizeToBase(symbol);
    const contractAtStart = get().contractByRoot[root];
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/flow-lines/${encodeURIComponent(symbol)}?names=${names.join(',')}&minutes=${minutes}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const lines = body && typeof body.lines === 'object' && body.lines ? body.lines : {};
      const contract = typeof body?.contract === 'string' ? body.contract : null;
      // The root's contract switched while this was in flight (live points of
      // the new one arrived): an answer for another contract is stale.
      const now = get().contractByRoot[root];
      if (now !== contractAtStart && contract !== null && contract !== now) return;
      // Merge, not replace: live points may have landed while this was in flight.
      get().addHistory(symbol, lines, contract);
    } catch (e) {
      if (!warned) {
        warned = true;
        console.warn('[FlowLines] history unavailable (engine not deployed yet?)', e);
      }
    }
  },

  watchLines: (symbol, names) => {
    if (!symbol || names.length === 0) return () => {};
    const root = normalizeToBase(symbol);
    let w = watches.get(root);
    if (!w) {
      w = { symbol, count: 0, names: new Map(), loaded: new Set() };
      watches.set(root, w);
      subscribeRoom(symbol);
    }
    w.count += 1;
    const toLoad: FlowAnyLineName[] = [];
    for (const n of names) {
      w.names.set(n, (w.names.get(n) ?? 0) + 1);
      if (!w.loaded.has(n)) {
        w.loaded.add(n);
        toLoad.push(n);
      }
    }
    if (toLoad.length > 0) void get().fetchLines(w.symbol, toLoad);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const cur = watches.get(root);
      if (!cur) return;
      for (const n of names) cur.names.set(n, (cur.names.get(n) ?? 1) - 1);
      cur.count -= 1;
      // Settled after the current task: a chart that swaps its line set
      // unwatches and rewatches in one commit, which must neither leave and
      // rejoin the room nor reload lines it already has.
      queueMicrotask(() => prune(root, cur));
    };
  },

  initialize: () => {
    users += 1;
    if (users > 1) return;
    bindSocket();
    /** Rejoin every watched room (a new socket has none). */
    const rejoin = () => {
      for (const w of watches.values()) if (w.count > 0) subscribeRoom(w.symbol);
    };
    /** Backfill every watched line from the newest point held. */
    const backfill = () => {
      const t = Date.now();
      for (const [root, w] of watches) {
        if (w.count <= 0) continue; // leaving
        const names = watchedNames(w);
        if (names.length === 0) continue;
        void get().fetchLines(w.symbol, names, backfillMinutes(get().byRoot[root], names, t));
      }
    };
    let prev = useMarketStore.getState().connectionStatus;
    unsubMarket = useMarketStore.subscribe((state) => {
      const now = state.connectionStatus;
      if (now === prev) return;
      prev = now;
      if (now !== 'connected') return;
      // A new socket has no rooms: rejoin now, so live points flow again.
      bindSocket();
      rejoin();
    });
    // The gap (socket down, relay down, or both) comes over REST, once per
    // reconnect: onFlowHistoryGap coalesces an engine restart's socket-back
    // and relay-back into one refetch.
    unsubRelay = onFlowHistoryGap(backfill);
  },

  teardown: () => {
    if (users === 0) return;
    users -= 1;
    if (users > 0) return;
    unsubMarket?.();
    unsubMarket = null;
    unsubRelay?.();
    unsubRelay = null;
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = null;
    pending = [];
    wsService.off('flow-line', onLine);
  },
}));
